import { Injectable } from '@angular/core';
import { GICSCodesUtil } from '@modeso/aumico-lib-core-fe';
import { RowTypes } from '@modeso/aumico-lib-annualreport-fe';
import type { ICustomerData } from '@modeso/aumico-lib-annualreport-fe';
import {
    IMultiYearComparisonResponseDTO,
    IMultiYearNodeDTO,
    IMultiYearYearPeriodDTO,
    IStyleOptions,
} from '@modeso/types__aumico-lib-annualreport-be';
import type { INameId } from '@modeso/types__aumico-lib-core-be';
import { ControlsState } from '../models/controls.model';
import {
    findNodeById,
    formatChfSignedRaw,
    formatCurrency,
    formatPercentageSignedRaw,
    formatStructPct,
    NO_DATA_PLACEHOLDER,
    ZERO_TOLERANCE,
} from '../models/multi-year-format.util';
import {
    IMultiYearPdfTronBalanceSheet,
    IMultiYearPdfTronData,
    IMultiYearPdfTronIncomeStatement,
    IMultiYearPdfTronRow,
} from './multi-year-pdf-tron-data.interface';

/** I18n labels resolved by the caller (effect). Keeps the mapper DI-free. */
export interface I18nLabelsForPdf {
    /** "in" / "en" — preposition before the currency code in the TableHead. */
    wordBeforeCurrency: string;
    /** Section titles, rendered above the table by the .docx template. */
    sectionTitleBalance: string;
    sectionTitleIncome: string;
    /** Δ column heading — e.g. "Gesamt Δ". */
    tableDeltaLabel: string;
}

/** Optional knobs passed from the export modal. */
export interface MultiYearPdfMapOptions {
    /** Strict per-row whitelist; `undefined` = no filter. Descendants are walked
     *  regardless, but ancestors are not auto-emitted from a deeper match. */
    selectedNodeIds?: ReadonlySet<string>;

    /** Gates `Acc` row emission under FLIs / SUB_FLIs. Accounts aren't selectable
     *  in the modal tree — this single switch covers them globally. */
    showAccounts?: boolean;

    /** "Bestandteile" section gates. `false` → empty `data` for that section
     *  → `hasBs`/`hasIs` false → .docx template skips the block. Default true. */
    includeBalance?: boolean;
    includeIncome?: boolean;

    /** Modal override for the Verhältniskennzahl column. Falls back to the
     *  in-app `controls.structMode` when undefined. */
    showRatios?: boolean;

    /** Pre-resolved color theme. Caller (effect) handles the `useCustomColors`
     *  → report-or-organization fallback; mapper just embeds the result. */
    resolvedColors?: IStyleOptions;
}

/**
 * Multi-year PDFTron mapper. Output mirrors the on-screen Mehrjahresvergleich:
 * flat `data` arrays per section, section roots as `GroupHeader` at level 0,
 * EmptyRow between Aktiven and Passiven, no GroupTotal / TableTotal. Strict
 * per-row selection (no cascade). Values are read pre-aggregated from
 * `IMultiYearNodeDTO.values[year]` — no re-summing here.
 */
@Injectable({ providedIn: 'root' })
export class MultiYearPdfTronSwissMapper {
    /** Static GICS lookup, computed once per process. */
    private readonly gicsCodesAndNames: INameId[] = GICSCodesUtil.getAllGICSCodesAndSubindustryNames();

    public map(
        harmonized: IMultiYearComparisonResponseDTO,
        controls: ControlsState,
        labels: I18nLabelsForPdf,
        options: MultiYearPdfMapOptions = {},
    ): IMultiYearPdfTronData {
        const ctx = this.buildContext(harmonized, controls, labels, options);
        // Section gates: when the modal user has switched a section OFF in
        // "Bestandteile", we emit an empty `data` array for it — this drops
        // even the leading `TableHead`, so `hasBs` / `hasIs` come out `false`
        // and the .docx template skips the whole block.
        const bs = options.includeBalance === false
            ? this.emptySection(ctx)
            : this.buildBs(harmonized, ctx);
        const is = options.includeIncome === false
            ? this.emptySection(ctx)
            : this.buildIs(harmonized, ctx);
        return {
            bs,
            is,
            // `hasBs` / `hasIs`: false when only the TableHead survived (or the
            // section was gated off) → .docx template skips the block.
            hasBs: bs.data.length > 1,
            hasIs: is.data.length > 1,
            sectionToTitle: {
                BALANCE: this.resolveSectionTitle(harmonized, 'balance', labels.sectionTitleBalance),
                INCOME: this.resolveSectionTitle(harmonized, 'income', labels.sectionTitleIncome),
            },
            colors: options.resolvedColors ?? harmonized.styleOptions,
            customer: this.getCustomerData(harmonized),
        };
    }

    /** `customer` block from the master AR's company. Empty-string fallbacks
     *  keep the .docx template free of `?? ''` gymnastics. */
    private getCustomerData(harmonized: IMultiYearComparisonResponseDTO): ICustomerData | undefined {
        const company = harmonized.company;
        if (!company) return undefined;
        const gics = this.gicsCodesAndNames.find((c) => c.id === company.GICSCode);
        return {
            companyName: company.name ?? '',
            address: company.street ?? '',
            address2: company.additionToAddress ?? '',
            zip: company.zip ?? '',
            country: company.country?.name ?? '',
            city: company.city ?? '',
            industry: gics?.name ?? '',
            taxDomicile: company.taxDomicile ?? '',
            uid: company.UID ?? '',
        };
    }

    /** Master AR's `reportSectionsTitles[key][locale]` wins; falls back to the
     *  FE i18n default. Locale is lower-cased ("de-ch") to match storage. */
    private resolveSectionTitle(
        harmonized: IMultiYearComparisonResponseDTO,
        sectionKey: 'balance' | 'income',
        fallback: string,
    ): string {
        const locale = (harmonized.locale ?? 'de-CH').toLowerCase();
        return harmonized.reportSectionsTitles?.[sectionKey]?.[locale] ?? fallback;
    }

    /** Empty section payload used when a section is gated off in the modal. */
    private emptySection(ctx: BuildContext): IMultiYearPdfTronBalanceSheet {
        return {
            yearCount: ctx.selectedYearIndices.length,
            showRatios: ctx.showRatios,
            deltaMode: ctx.controls.deltaMode,
            data: [],
        };
    }

    // ─── Section builders ───────────────────────────────────────────────────

    private buildBs(harmonized: IMultiYearComparisonResponseDTO, ctx: BuildContext): IMultiYearPdfTronBalanceSheet {
        const bsHeaders = ctx.selectedYearIndices.map((i) =>
            this.formatBalanceSheetDate(harmonized.yearPeriods[i], harmonized.yearLabels[i]),
        );

        // BS uses a *per-section* Verhältniskennzahl base — every line in
        // Aktiven is measured against Total Aktiven, every line in Passiven
        // against Total Passiven. Mirrors the on-screen view.
        const assetsCtx = this.withSectionReference(ctx, harmonized.assets);
        const liabilitiesCtx = this.withSectionReference(ctx, harmonized.liabilities);

        const data: IMultiYearPdfTronRow[] = [];
        data.push(this.buildTableHeadRow(this.buildCurrencyHeader(harmonized, ctx), bsHeaders, ctx));

        const assetsCount = data.length;
        this.appendSectionBlock(harmonized.assets, assetsCtx, data, /* isIncomeStatement */ false);
        const assetsHadContent = data.length > assetsCount;

        const beforeLiabilities = data.length;
        this.appendSectionBlock(harmonized.liabilities, liabilitiesCtx, data, false);
        const liabilitiesHadContent = data.length > beforeLiabilities;

        // Insert an EmptyRow gap between the two BS sections, only when both
        // contributed at least one row. Mirrors the on-screen view's
        // `{ kind: 'gap' }` divider.
        if (assetsHadContent && liabilitiesHadContent) {
            data.splice(beforeLiabilities, 0, this.buildEmptyRow());
        }

        return {
            yearCount: ctx.selectedYearIndices.length,
            showRatios: ctx.showRatios,
            deltaMode: ctx.controls.deltaMode,
            data,
        };
    }

    private buildIs(harmonized: IMultiYearComparisonResponseDTO, ctx: BuildContext): IMultiYearPdfTronIncomeStatement {
        const isHeaders = ctx.selectedYearIndices.map((i) =>
            this.formatIncomePeriod(harmonized.yearPeriods[i], harmonized.yearLabels[i]),
        );

        const data: IMultiYearPdfTronRow[] = [];
        data.push(this.buildTableHeadRow(this.buildCurrencyHeader(harmonized, ctx), isHeaders, ctx));

        if (harmonized.incomeStatement) {
            // ER: descendants top-down at level 1+, then the section root at
            // the BOTTOM as a level-0 GroupHeader (same as on-screen `isErTotal`).
            for (const child of harmonized.incomeStatement.children) {
                this.appendNode(child, ctx, data, /* level */ 1);
            }
            if (this.passesAll(harmonized.incomeStatement, ctx)) {
                data.push(this.buildValueRow(harmonized.incomeStatement, RowTypes.GROUP_HEADER, ctx, /* level */ 0));
            }
        }

        return {
            yearCount: ctx.selectedYearIndices.length,
            showRatios: ctx.showRatios,
            deltaMode: ctx.controls.deltaMode,
            data,
        };
    }

    /**
     * Append one BS section block (Aktiven or Passiven) to `out`. Section root
     * is emitted as a `GroupHeader` at level 0, then descendants at level 1+. No
     * GroupTotal, no TableTotal — same flat shape as the on-screen view.
     */
    private appendSectionBlock(
        sectionRoot: IMultiYearNodeDTO | null,
        ctx: BuildContext,
        out: IMultiYearPdfTronRow[],
        isIncomeStatement: boolean,
    ): void {
        if (sectionRoot == null) return;

        if (this.passesAll(sectionRoot, ctx)) {
            out.push(this.buildValueRow(sectionRoot, RowTypes.GROUP_HEADER, ctx, /* level */ 0));
        }
        for (const child of sectionRoot.children) {
            this.appendNode(child, ctx, out, /* level */ 1);
        }
    }

    // ─── Recursive node walker ──────────────────────────────────────────────

    /** Strict per-row emission via `passesAll`. Children are walked even when
     *  the parent didn't emit — descendant selection doesn't drag in ancestors. */
    private appendNode(
        node: IMultiYearNodeDTO,
        ctx: BuildContext,
        out: IMultiYearPdfTronRow[],
        level: number,
    ): void {
        switch (node.type) {
            case 'GROUP':
                if (this.passesAll(node, ctx)) {
                    out.push(this.buildValueRow(node, RowTypes.GROUP_HEADER, ctx, level));
                }
                for (const child of node.children) {
                    this.appendNode(child, ctx, out, level + 1);
                }
                return;

            case 'FLI':
                {
                    const fliEmitted = this.passesAll(node, ctx);
                    if (fliEmitted) {
                        out.push(this.buildValueRow(node, RowTypes.ROW, ctx, level));
                    }
                    // Walk children. SUB_FLI / OF use the regular selection
                    // filter. Accounts use the global `showAccounts` toggle —
                    // they only appear when the toggle is ON AND their parent
                    // FLI / SUB_FLI was emitted (otherwise they'd dangle).
                    for (const child of node.children) {
                        if (child.type === 'ACCOUNT') {
                            if (fliEmitted && this.shouldEmitAccount(child, ctx)) {
                                out.push(this.buildValueRow(child, RowTypes.ACCOUNT, ctx, level + 1, this.accountLabel(child)));
                            }
                        } else if (child.type === 'SUB_FLI' || child.type === 'OF_THAT_ITEM') {
                            const subEmitted = this.passesAll(child, ctx);
                            if (subEmitted) {
                                out.push(this.buildValueRow(child, RowTypes.SUB_ROW, ctx, level + 1));
                            }
                            for (const acc of child.children) {
                                if (acc.type === 'ACCOUNT' && subEmitted && this.shouldEmitAccount(acc, ctx)) {
                                    out.push(this.buildValueRow(acc, RowTypes.ACCOUNT, ctx, level + 2, this.accountLabel(acc)));
                                }
                            }
                        }
                    }
                }
                return;

            case 'SUB_FLI':
            case 'OF_THAT_ITEM':
                // Top-level SUB_FLI / OF (sub-FLI sitting directly under a GROUP).
                {
                    const subEmitted = this.passesAll(node, ctx);
                    if (subEmitted) {
                        out.push(this.buildValueRow(node, RowTypes.SUB_ROW, ctx, level));
                    }
                    for (const acc of node.children) {
                        if (acc.type === 'ACCOUNT' && subEmitted && this.shouldEmitAccount(acc, ctx)) {
                            out.push(this.buildValueRow(acc, RowTypes.ACCOUNT, ctx, level + 1, this.accountLabel(acc)));
                        }
                    }
                }
                return;

            case 'SUB_TOTAL':
                if (this.passesAll(node, ctx)) {
                    out.push(this.buildTotalRow(node, RowTypes.SUB_TOTAL, ctx, /* labelOverride */ node.label, level));
                }
                return;

            case 'ACCOUNT':
                // Defensive: account directly under a GROUP (no wrapping FLI).
                // Same `showAccounts` gate as nested accounts — there's no
                // FLI parent to anchor "visibility" on, so the toggle alone
                // controls emission.
                if (this.shouldEmitAccount(node, ctx)) {
                    out.push(this.buildValueRow(node, RowTypes.ACCOUNT, ctx, level, this.accountLabel(node)));
                }
                return;
        }
    }

    /** Selectable-row gate: must be in the user's selection AND not all-zero across selected years. */
    private passesAll(node: IMultiYearNodeDTO, ctx: BuildContext): boolean {
        if (this.isAllZeroAcrossSelected(node, ctx) && this.hasNoNonZeroDescendant(node, ctx)) {
            return false;
        }
        return this.isSelected(node, ctx);
    }

    /** Accounts: gated globally by `showAccounts`; zero-filter still applies. */
    private shouldEmitAccount(node: IMultiYearNodeDTO, ctx: BuildContext): boolean {
        if (!ctx.showAccounts) return false;
        if (this.isAllZeroAcrossSelected(node, ctx) && this.hasNoNonZeroDescendant(node, ctx)) {
            return false;
        }
        return true;
    }

    // ─── Row factories ──────────────────────────────────────────────────────

    /** "in CHF" / "in EUR" label for the TableHead's `t` field. Currency picked
     *  from the newest selected year. Section title itself is rendered above
     *  the table by the .docx template via `{sectionToTitle.*}`. */
    private buildCurrencyHeader(harmonized: IMultiYearComparisonResponseDTO, ctx: BuildContext): string {
        const newest = ctx.selectedYearIndices[0];
        const currency = harmonized.currencies[newest] ?? harmonized.currencies[0] ?? '';
        if (currency === '') return ctx.labels.wordBeforeCurrency;
        return `${ctx.labels.wordBeforeCurrency} ${currency}`;
    }

    /** `TableHead`: column dates in `col*V`, `%` glyph in `col*P` when ratios on,
     *  `Gesamt Δ` label in `delta`. Always at level 0. */
    private buildTableHeadRow(title: string, columnHeaders: string[], ctx: BuildContext): IMultiYearPdfTronRow {
        const colsV = this.padColsToSix(columnHeaders);
        // Only fill ratio-column headers for the columns that actually have a date —
        // padding columns stay empty so the template's column-visibility logic still works.
        const colsP = this.padColsToSix(
            ctx.showRatios ? columnHeaders.map(() => '%') : [],
        );
        return {
            rowType: RowTypes.TABLE_HEAD,
            t: title,
            level: 0,
            ...this.buildColsFromValues(colsV, colsP),
            delta: ctx.labels.tableDeltaLabel,
        };
    }

    private buildValueRow(
        node: IMultiYearNodeDTO,
        rowType: RowTypes,
        ctx: BuildContext,
        level: number,
        labelOverride?: string,
    ): IMultiYearPdfTronRow {
        const valuesPerColumn = ctx.selectedYearIndices.map((yearIdx, columnIdx) => {
            const raw = node.values[yearIdx] ?? 0;
            return {
                v: formatCurrency(raw),
                p: ctx.showRatios
                    ? formatStructPct(raw, ctx.referenceValues[columnIdx], node.isReferenceValue)
                    : '',
            };
        });
        const cols = this.buildColsFromPairs(valuesPerColumn);
        const delta = this.buildDelta(node, ctx);
        return {
            rowType,
            t: labelOverride ?? node.label,
            level,
            ...cols,
            ...delta,
        };
    }

    /** Compose the `t` label for an account row: "<number> <name>" when the
     *  account has a number (real GL account), bare name for synthetic accounts
     *  (Gewinn / shortfall — accountNumber empty). Same shape the on-screen
     *  view shows in two separate spans. */
    private accountLabel(node: IMultiYearNodeDTO): string {
        return node.accountNumber ? `${node.accountNumber} ${node.label}` : node.label;
    }

    private buildTotalRow(
        node: IMultiYearNodeDTO,
        rowType: RowTypes,
        ctx: BuildContext,
        labelOverride: string,
        level: number,
    ): IMultiYearPdfTronRow {
        const valuesPerColumn = ctx.selectedYearIndices.map((yearIdx, columnIdx) => {
            const raw = node.values[yearIdx] ?? 0;
            return {
                v: formatCurrency(raw),
                // Same ratio rule as `buildValueRow`: totals get the
                // Verhältniskennzahl too when `structMode` is on.
                p: ctx.showRatios
                    ? formatStructPct(raw, ctx.referenceValues[columnIdx], node.isReferenceValue)
                    : '',
            };
        });
        const cols = this.buildColsFromPairs(valuesPerColumn);
        const delta = this.buildDelta(node, ctx);
        return {
            rowType,
            t: labelOverride,
            level,
            ...cols,
            ...delta,
        };
    }

    // ─── Column helpers ─────────────────────────────────────────────────────

    private buildColsFromPairs(pairs: { v: string; p: string }[]): ColPairs {
        const padded = this.padPairsToSix(pairs);
        return {
            col0V: padded[0].v, col0P: padded[0].p,
            col1V: padded[1].v, col1P: padded[1].p,
            col2V: padded[2].v, col2P: padded[2].p,
            col3V: padded[3].v, col3P: padded[3].p,
            col4V: padded[4].v, col4P: padded[4].p,
            col5V: padded[5].v, col5P: padded[5].p,
        };
    }

    private buildColsFromValues(colsV: string[], colsP: string[]): ColPairs {
        return {
            col0V: colsV[0], col0P: colsP[0],
            col1V: colsV[1], col1P: colsP[1],
            col2V: colsV[2], col2P: colsP[2],
            col3V: colsV[3], col3P: colsP[3],
            col4V: colsV[4], col4P: colsP[4],
            col5V: colsV[5], col5P: colsP[5],
        };
    }

    private padColsToSix(values: string[]): string[] {
        const out = values.slice(0, 6);
        while (out.length < 6) out.push('');
        return out;
    }

    private padPairsToSix(pairs: { v: string; p: string }[]): { v: string; p: string }[] {
        const out = pairs.slice(0, 6);
        while (out.length < 6) out.push({ v: '', p: '' });
        return out;
    }

    // ─── Δ helper (mirrors row-vm.builder buildDeltaCell) ───────────────────

    /**
     * Single `delta` value, formatted unit-less and selected by `deltaMode`:
     *   - 'pct' → percentage form ("+12.5" / "-3.4")
     *   - 'chf' → absolute form  ("+1'234.00" / "-12'345.00")
     * The unit lives in the template column header so the cell stays unit-less.
     * `NO_DATA_PLACEHOLDER` ("-") returned when the oldest year is zero
     * (division-by-zero guard, matches the on-screen `row-vm.builder` rule).
     */
    private buildDelta(node: IMultiYearNodeDTO, ctx: BuildContext): { delta: string } {
        const newest = node.values[ctx.newestIdx] ?? 0;
        const oldest = node.values[ctx.oldestIdx] ?? 0;

        if (Math.abs(oldest) < ZERO_TOLERANCE) {
            return { delta: NO_DATA_PLACEHOLDER };
        }

        const magnitudeChf = Math.abs(newest) - Math.abs(oldest);
        const magnitudePct = (magnitudeChf / Math.abs(oldest)) * 100;
        return {
            delta: ctx.controls.deltaMode === 'pct'
                ? formatPercentageSignedRaw(magnitudePct)
                : formatChfSignedRaw(magnitudeChf),
        };
    }

    // ─── Date / period header formatters ────────────────────────────────────

    private formatBalanceSheetDate(period: IMultiYearYearPeriodDTO | undefined, fallback: string): string {
        if (!period?.balanceSheetDate) return fallback;
        return this.formatIsoDate(period.balanceSheetDate);
    }

    private formatIncomePeriod(period: IMultiYearYearPeriodDTO | undefined, fallback: string): string {
        if (!period?.incomeFromDate || !period?.incomeToDate) return fallback;
        // `\n` becomes a `<w:br/>` via docxtemplater's `linebreaks: true` — splits
        // the range header into two visual lines with the dash anchoring line 2:
        //   01.01.2023
        //   – 31.12.2023
        return `${this.formatIsoDate(period.incomeFromDate)}\n– ${this.formatIsoDate(period.incomeToDate)}`;
    }

    /** ISO `YYYY-MM-DD` → Swiss `DD.MM.YYYY`. */
    private formatIsoDate(iso: string): string {
        const [y, m, d] = iso.split('T')[0].split('-');
        if (!y || !m || !d) return iso;
        return `${d}.${m}.${y}`;
    }

    // ─── Misc ───────────────────────────────────────────────────────────────

    /** Visual gap between the Aktiven and Passiven blocks. Mirrors the on-screen
     *  view's section-gap divider. All cells empty so the template just renders
     *  a blank row when it sees `rowType === 'EmptyRow'`. */
    private buildEmptyRow(): IMultiYearPdfTronRow {
        return {
            rowType: RowTypes.EMPTY_ROW,
            t: '',
            level: 0,
            ...this.buildColsFromValues(this.padColsToSix([]), this.padColsToSix([])),
            delta: '',
        };
    }

    private isAllZeroAcrossSelected(node: IMultiYearNodeDTO, ctx: BuildContext): boolean {
        return ctx.selectedYearIndices.every((i) => Math.abs(node.values[i] ?? 0) < ZERO_TOLERANCE);
    }

    private hasNoNonZeroDescendant(node: IMultiYearNodeDTO, ctx: BuildContext): boolean {
        if (!this.isAllZeroAcrossSelected(node, ctx)) return false;
        if (node.children.length === 0) return true;
        return node.children.every((c) => this.hasNoNonZeroDescendant(c, ctx));
    }

    private buildContext(
        harmonized: IMultiYearComparisonResponseDTO,
        controls: ControlsState,
        labels: I18nLabelsForPdf,
        options: MultiYearPdfMapOptions,
    ): BuildContext {
        const selectedSet = new Set(controls.selectedYears);
        const selectedYearIndices: number[] = [];
        harmonized.yearLabels.forEach((y, idx) => {
            if (selectedSet.has(y)) selectedYearIndices.push(idx);
        });

        const newestIdx = selectedYearIndices[0];
        const oldestIdx = selectedYearIndices[selectedYearIndices.length - 1];

        const referenceNode = harmonized.referenceNodeId
            ? findNodeById(harmonized.incomeStatement, harmonized.referenceNodeId)
            : null;
        const referenceValues = selectedYearIndices.map((i) => referenceNode?.values[i] ?? 0);

        // Modal override for ratios falls back to the in-app `structMode`
        // when not provided — preserves legacy behavior for callers that
        // don't go through the modal.
        const showRatios = options.showRatios !== undefined ? options.showRatios : controls.structMode;

        return {
            controls,
            labels,
            selectedYearIndices,
            newestIdx,
            oldestIdx,
            referenceValues,
            selectedNodeIds: options.selectedNodeIds,
            showAccounts: !!options.showAccounts,
            showRatios,
        };
    }

    /** `node.id` must be in the modal's selection set. Undefined set = no filter. */
    private isSelected(node: IMultiYearNodeDTO, ctx: BuildContext): boolean {
        if (!ctx.selectedNodeIds) return true;
        return ctx.selectedNodeIds.has(node.id);
    }

    /** Per-BS-section ratio base: each Aktiven/Passiven row is measured against
     *  its own section total, not the ER's Betriebsertrag. */
    private withSectionReference(ctx: BuildContext, sectionRoot: IMultiYearNodeDTO | null): BuildContext {
        const referenceValues = ctx.selectedYearIndices.map((i) => sectionRoot?.values[i] ?? 0);
        return { ...ctx, referenceValues };
    }
}

interface BuildContext {
    controls: ControlsState;
    labels: I18nLabelsForPdf;
    selectedYearIndices: number[];
    newestIdx: number;
    oldestIdx: number;
    referenceValues: number[];
    selectedNodeIds?: ReadonlySet<string>;
    showAccounts: boolean;
    /** Modal override → falls back to `controls.structMode`. */
    showRatios: boolean;
}

interface ColPairs {
    col0V: string; col0P: string;
    col1V: string; col1P: string;
    col2V: string; col2P: string;
    col3V: string; col3P: string;
    col4V: string; col4P: string;
    col5V: string; col5P: string;
}
